import React, { useEffect, useState } from "react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "react-datepicker/dist/react-datepicker.css";
import "./onlineLecture.css";

import SideBar from "../../components/SideBar";
import BackButton from "../../components/Base/BackButton";
import Loader from "../../components/Loader";
import { Components } from "../../assets/Dimensions";

import {
    updateOnlineLecture,
    updateGeneralLecture,
    deleteOnlineLecture,
    deleteOnlineLectureHomeWork,
    onlineLectureClearCache,
    onlineLectureUpdateUrlType,
} from "../../api/onlineLecture";
import ResponseStatus from "../../util/enums";

import { useLectureData } from "./hooks/useLectureData";
import { useSegmentCatalog } from "./hooks/useSegmentCatalog";
import { dateChange } from "./lib/dates";
import { UrlType, URL_TYPE_STORAGE_KEY } from "./constants";

import LectureTable from "./components/LectureTable";
import ConfirmDialog from "./components/ConfirmDialog";
import AddLectureModal from "./components/modals/AddLectureModal";
import AddMutualLectureModal from "./components/modals/AddMutualLectureModal";
import UpdateLectureModal from "./components/modals/UpdateLectureModal";
import HomeworkModal from "./components/modals/HomeworkModal";
import DateChangeModal from "./components/modals/DateChangeModal";

// Online Lecture admin panel — container. Owns server data, the segment
// catalog, and modal orchestration. Modals are only mounted while open, so
// closing one discards its transient state (replicates the legacy reset
// matrix); the catalog is reset explicitly when the normal-add modal closes.
const OnlineLecturePanel = () => {
    const user = JSON.parse(localStorage.getItem("user"));
    const adminToken = sessionStorage.getItem("jwtP");
    const cmsToken = user ? user.Token : null;

    const { lectureList, teacherList, loading, setLoading, refreshLectures, refreshTeachers } =
        useLectureData(adminToken);
    const catalog = useSegmentCatalog(cmsToken);

    // Sticky default for the add-forms' UrlType select (legacy localStorage key)
    const [urlTypeDefault, setUrlTypeDefault] = useState(UrlType.None);
    useEffect(() => {
        const storedValue = localStorage.getItem(URL_TYPE_STORAGE_KEY);
        if (storedValue) {
            setUrlTypeDefault(parseInt(storedValue, 10));
        }
    }, []);
    const handleUrlTypeDefaultChange = (value) => {
        setUrlTypeDefault(value);
        localStorage.setItem(URL_TYPE_STORAGE_KEY, value);
    };

    // Modal orchestration — only one dialog is ever open at a time
    const [addMenuOpen, setAddMenuOpen] = useState(false);
    const [addNormalOpen, setAddNormalOpen] = useState(false);
    const [addMutualOpen, setAddMutualOpen] = useState(false);
    const [updateTarget, setUpdateTarget] = useState(null);
    const [homeworkModal, setHomeworkModal] = useState(null); // { lecture, homework, isUpdate }
    const [deleteLectureTarget, setDeleteLectureTarget] = useState(null);
    const [deleteHomeworkTarget, setDeleteHomeworkTarget] = useState(null);
    const [dateFlow, setDateFlow] = useState(null); // { lecture, step: 'pick' | 'confirm', date }
    const [cacheConfirmOpen, setCacheConfirmOpen] = useState(false);
    const [duplicateSource, setDuplicateSource] = useState(null); // lecture being duplicated

    useEffect(() => {
        refreshLectures();
        refreshTeachers();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ----- actions -----

    const closeAddNormal = () => {
        setAddNormalOpen(false);
        setDuplicateSource(null);
        catalog.reset();
    };

    const closeAddMutual = () => {
        setAddMutualOpen(false);
        setDuplicateSource(null);
    };

    // "Çoğalt": open the matching add modal prefilled from the source lecture —
    // only the date (and the attestation checkbox) are left for the operator.
    // General lectures route to the mutual modal (SubCourseTopicUniqueId == 0);
    // curriculum lectures get their cascade resolved from the segment APIs.
    const duplicateLecture = (item) => {
        setDuplicateSource(item);
        if (item.SubCourseTopicUniqueId == 0) {
            setAddMutualOpen(true);
        } else {
            catalog.prefillFromLecture(item);
            setAddNormalOpen(true);
        }
    };

    // Date-change update. NOTE: this flow routes general lectures by
    // CourseId == 0 (the update modal uses SubCourseTopicUniqueId == 0) —
    // two distinct legacy discriminators, kept as-is.
    const updateLectureDate = async (lecture, formattedDate) => {
        setLoading(true);
        const updatedPayload = { ...lecture, Date: formattedDate };
        const formData = new FormData();
        formData.append("OnlineLectureTeacherId", updatedPayload.TeacherInfos.TeacherId);
        for (const key in updatedPayload) {
            if (key !== "Homeworks" && key !== "TeacherInfos" && updatedPayload[key] != null) {
                formData.append(key, updatedPayload[key]);
            }
        }
        const res = await (lecture.CourseId == 0
            ? updateGeneralLecture(formData, adminToken)
            : updateOnlineLecture(formData, adminToken));
        if (res && res.data && res.data.ResponseStatus == ResponseStatus.Success) {
            toast.success("Ders Güncelleme başarılı");
            refreshLectures();
        } else {
            toast.error(res && res.data ? res.data.ResponseMessage : "Bir hata oluştu");
        }
        setLoading(false);
    };

    const deleteLecture = async (id) => {
        setLoading(true);
        const res = await deleteOnlineLecture({ Id: id }, adminToken);
        if (res.status == 200) {
            toast.success("ders kaldırılmıştır");
            refreshLectures();
        }
        setLoading(false);
    };

    const deleteHomework = async (id) => {
        setLoading(true);
        const res = await deleteOnlineLectureHomeWork(id, adminToken);
        if (res.status == 200) {
            toast.success("Ödev kaldırılmıştır");
            refreshLectures();
        }
        setLoading(false);
    };

    // "Sisteme Yükle" — clears the backend online-lecture cache; the only way
    // edits become visible to app users
    const clearCache = async () => {
        const res = await onlineLectureClearCache(adminToken);
        if (res.status == 200) {
            toast.success("Sistem Güncellenmiştir");
            refreshLectures();
        }
    };

    // Instant per-row url-type change (no confirmation — legacy behavior)
    const handleRowUrlTypeChange = async (event, item) => {
        const requestData = {
            OnlineLectureId: item.Id,
            OfflineUrlType: event.target.value,
        };
        await onlineLectureUpdateUrlType(requestData, adminToken);
        refreshLectures();
    };

    const containerStyle = {
        marginLeft: Components.SideBarWidth + 15,
        paddingTop: 20,
        marginRight: 30,
    };

    return (
        <div className="olp-theme olp-page">
            <SideBar />
            <ToastContainer />
            <div style={containerStyle}>
                <BackButton />
                <div className="olp-header">
                    <h1 className="olp-title">Canlı Ders Paneli</h1>
                    <div className="olp-header-actions">
                        <button className="olp-btn" onClick={() => window.open("/addTeachers")}>
                            Öğretmen ekle
                        </button>
                        <button className="olp-btn olp-btn--primary" onClick={() => setAddMenuOpen(!addMenuOpen)}>
                            Ders ekle
                        </button>
                        {addMenuOpen && (
                            <div className="olp-add-menu">
                                <button className="olp-btn" onClick={() => setAddNormalOpen(true)}>
                                    Normal Ders Ekle
                                </button>
                                <button className="olp-btn" onClick={() => setAddMutualOpen(true)}>
                                    Rehberlik ve ortak dersler ekle
                                </button>
                            </div>
                        )}
                    </div>
                </div>

                <LectureTable
                    lectures={lectureList}
                    onEditDate={(item) => setDateFlow({ lecture: item, step: "pick", date: new Date() })}
                    onEditLecture={(item) => setUpdateTarget(item)}
                    onDuplicateLecture={duplicateLecture}
                    onAddHomework={(item) => setHomeworkModal({ lecture: item, homework: null, isUpdate: false })}
                    onEditHomework={(item, homeWork) =>
                        setHomeworkModal({ lecture: item, homework: homeWork, isUpdate: true })
                    }
                    onDeleteHomework={(homeWork) => setDeleteHomeworkTarget(homeWork)}
                    onDeleteLecture={(item) => setDeleteLectureTarget(item)}
                    onUrlTypeChange={handleRowUrlTypeChange}
                />
            </div>

            <button className="olp-btn olp-btn--success olp-publish-btn" onClick={() => setCacheConfirmOpen(true)}>
                Sisteme Yükle
            </button>

            {/* ----- dialogs (one at a time) ----- */}

            {addNormalOpen && (
                <AddLectureModal
                    open={addNormalOpen}
                    onClose={closeAddNormal}
                    teachers={teacherList}
                    catalog={catalog}
                    adminToken={adminToken}
                    urlType={urlTypeDefault}
                    onUrlTypeChange={handleUrlTypeDefaultChange}
                    onAdded={refreshLectures}
                    duplicateSource={duplicateSource}
                />
            )}

            {addMutualOpen && (
                <AddMutualLectureModal
                    open={addMutualOpen}
                    onClose={closeAddMutual}
                    teachers={teacherList}
                    adminToken={adminToken}
                    urlType={urlTypeDefault}
                    onUrlTypeChange={handleUrlTypeDefaultChange}
                    onAdded={refreshLectures}
                    duplicateSource={duplicateSource}
                />
            )}

            {updateTarget && (
                <UpdateLectureModal
                    open={Boolean(updateTarget)}
                    onClose={() => setUpdateTarget(null)}
                    lecture={updateTarget}
                    segments={catalog.segments}
                    adminToken={adminToken}
                    onUpdated={refreshLectures}
                />
            )}

            {homeworkModal && (
                <HomeworkModal
                    open={Boolean(homeworkModal)}
                    onClose={() => setHomeworkModal(null)}
                    lecture={homeworkModal.lecture}
                    homework={homeworkModal.homework}
                    isUpdate={homeworkModal.isUpdate}
                    adminToken={adminToken}
                    onSaved={refreshLectures}
                />
            )}

            {deleteLectureTarget && (
                <ConfirmDialog
                    open={Boolean(deleteLectureTarget)}
                    onClose={() => setDeleteLectureTarget(null)}
                    imageUrl={deleteLectureTarget.ThumbnailUrl}
                    lines={[deleteLectureTarget.SubTopicName]}
                    text="Bu canlı dersi silmeyi onaylıyormusunuz"
                    confirmLabel="Sil"
                    confirmVariant="olp-btn--danger-solid"
                    onConfirm={() => {
                        deleteLecture(deleteLectureTarget.Id);
                        setDeleteLectureTarget(null);
                    }}
                />
            )}

            {deleteHomeworkTarget && (
                <ConfirmDialog
                    open={Boolean(deleteHomeworkTarget)}
                    onClose={() => setDeleteHomeworkTarget(null)}
                    lines={[deleteHomeworkTarget.Title, deleteHomeworkTarget.Description]}
                    text="bu ödevi silmeyi onaylıyormusunuz?"
                    confirmLabel="Sil"
                    confirmVariant="olp-btn--danger-solid"
                    onConfirm={() => {
                        deleteHomework(deleteHomeworkTarget.Id);
                        setDeleteHomeworkTarget(null);
                    }}
                />
            )}

            {dateFlow && dateFlow.step === "pick" && (
                <DateChangeModal
                    open
                    onClose={() => setDateFlow(null)}
                    startDate={dateFlow.date}
                    onDateChange={(date) => setDateFlow({ ...dateFlow, date })}
                    onNext={() => setDateFlow({ ...dateFlow, step: "confirm" })}
                />
            )}

            {dateFlow && dateFlow.step === "confirm" && (
                <ConfirmDialog
                    open
                    onClose={() => setDateFlow(null)}
                    imageUrl={dateFlow.lecture.ThumbnailUrl}
                    text="Bu canlı dersin tarihini değiştirmeyi onaylıyormusunuz"
                    confirmLabel="Onayla"
                    onConfirm={() => {
                        updateLectureDate(dateFlow.lecture, dateChange(dateFlow.date));
                        setDateFlow(null);
                    }}
                />
            )}

            {cacheConfirmOpen && (
                <ConfirmDialog
                    open={cacheConfirmOpen}
                    onClose={() => setCacheConfirmOpen(false)}
                    text="Değişiklikleriniz sisteme kaydedilecektir onaylıyormusunuz?"
                    confirmLabel="EVET"
                    cancelLabel="HAYIR"
                    confirmVariant="olp-btn--success"
                    onConfirm={() => {
                        clearCache();
                        setCacheConfirmOpen(false);
                    }}
                />
            )}

            {loading ? <Loader /> : null}
        </div>
    );
};

export default OnlineLecturePanel;
